import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import ContextualTooltip from '../ContextualTooltip';

const CollaboratorPicker = forwardRef(function CollaboratorPicker({
  listId,
  collaborators,
  onTransferOwnership,
  isOpen,
  user,
  isOwner,
  showAlert,
  showToast,
}, ref) {
  const [friends, setFriends] = useState([]);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [collaboratorDetails, setCollaboratorDetails] = useState([]);
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const pickerRef = useRef(null);
  const collaboratorInputRef = useRef(null);

  // Local staged changes - these are applied only on save
  const [pendingAdditions, setPendingAdditions] = useState([]); // Array of friend objects to add
  const [pendingRemovals, setPendingRemovals] = useState([]); // Array of collaborator userIds to remove

  const MAX_COLLABORATORS = 10;
  const originalCollaboratorIds = collaborators || [];

  // Compute effective collaborator list (original - removals + additions)
  const effectiveCollaboratorIds = [
    ...originalCollaboratorIds.filter(id => !pendingRemovals.includes(id)),
    ...pendingAdditions.map(f => f.id),
  ];

  // Reset pending changes when modal opens/closes or list changes
  useEffect(() => {
    if (isOpen) {
      setPendingAdditions([]);
      setPendingRemovals([]);
    }
  }, [isOpen, listId]);

  // Expose methods to parent for getting pending changes
  useImperativeHandle(ref, () => ({
    getPendingChanges: () => ({
      additions: pendingAdditions.map(f => f.id),
      removals: pendingRemovals,
    }),
    hasPendingChanges: () => pendingAdditions.length > 0 || pendingRemovals.length > 0,
    resetChanges: () => {
      setPendingAdditions([]);
      setPendingRemovals([]);
    },
  }));

  // Fetch friends when modal opens
  useEffect(() => {
    const fetchFriends = async () => {
      if (!isOpen || !user) return;

      setLoadingFriends(true);
      try {
        const currentUserId = user.id || user.userId;
        const response = await api.getFriends(currentUserId);
        const friendsList = (response.data || [])
          .map(item => item.user)
          .filter(Boolean);
        setFriends(friendsList);
      } catch (err) {
        console.error('Failed to fetch friends:', err);
      } finally {
        setLoadingFriends(false);
      }
    };

    fetchFriends();
  }, [isOpen, user]);

  // Fetch collaborator details
  useEffect(() => {
    const fetchCollaboratorDetails = async () => {
      if (!listId || originalCollaboratorIds.length === 0) {
        setCollaboratorDetails([]);
        return;
      }

      try {
        const response = await api.getCollaborators(listId);
        if (response.data) {
          setCollaboratorDetails(response.data.collaborators || []);
        }
      } catch (err) {
        console.error('Failed to fetch collaborator details:', err);
      }
    };

    fetchCollaboratorDetails();
  }, [listId, originalCollaboratorIds.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Filter friends based on search and exclude already collaborators (including pending additions)
  const filteredFriends = friends.filter((friend) => {
    const isNotCollaborator = !effectiveCollaboratorIds.includes(friend.id);
    const matchesSearch = collaboratorSearch === '' ||
      (friend.displayName || '').toLowerCase().includes(collaboratorSearch.toLowerCase()) ||
      (friend.username || '').toLowerCase().includes(collaboratorSearch.toLowerCase());
    return isNotCollaborator && matchesSearch;
  });

  // Build display list: existing collaborators (minus pending removals) + pending additions
  const displayCollaborators = [
    ...collaboratorDetails
      .filter(c => !pendingRemovals.includes(c.userId))
      .map(c => ({ ...c, isPending: false })),
    ...pendingAdditions.map(f => ({
      userId: f.id,
      displayName: f.displayName,
      username: f.username,
      profilePicture: f.profilePicture || f.photoURL,
      isPremium: f.isPremium,
      isPending: true,
    })),
  ];

  const handleAddCollaborator = (friend) => {
    if (effectiveCollaboratorIds.length >= MAX_COLLABORATORS) {
      showAlert(`Maximum of ${MAX_COLLABORATORS} collaborators per list`, { variant: 'warning' });
      return;
    }

    // If this was a previously removed collaborator, just undo the removal
    if (pendingRemovals.includes(friend.id)) {
      setPendingRemovals(prev => prev.filter(id => id !== friend.id));
    } else {
      // Add to pending additions
      setPendingAdditions(prev => [...prev, friend]);
    }

    setCollaboratorSearch('');
    setShowDropdown(false);
  };

  const handleRemoveCollaborator = (collaboratorId) => {
    // If this was a pending addition, just remove from pending
    const pendingAddition = pendingAdditions.find(f => f.id === collaboratorId);
    if (pendingAddition) {
      setPendingAdditions(prev => prev.filter(f => f.id !== collaboratorId));
    } else {
      // Mark as pending removal
      setPendingRemovals(prev => [...prev, collaboratorId]);
    }
  };

  const handleTransferClick = (collaborator) => {
    setTransferTarget(collaborator);
    setShowTransferConfirm(true);
  };

  const handleConfirmTransfer = async () => {
    if (!transferTarget || transferring) return;

    setTransferring(true);
    try {
      await onTransferOwnership(transferTarget.userId);
      setShowTransferConfirm(false);
      setTransferTarget(null);
      showToast('Ownership transferred successfully');
    } catch (err) {
      showAlert(err.message || 'Failed to transfer ownership', { variant: 'error' });
    } finally {
      setTransferring(false);
    }
  };

  // Don't show if not owner
  if (!isOwner) return null;

  return (
    <div className="form-group collaborator-section">
      <label>
        Collaborators{' '}
        <span className="collaborator-count">({effectiveCollaboratorIds.length} / {MAX_COLLABORATORS})</span>
      </label>
      <p className="form-helper-text">
        Collaborators can add, edit, and remove places from this list. You remain the owner with sole ability to delete the list, change visibility, or transfer ownership.
      </p>

      {/* Current collaborators */}
      {displayCollaborators.length > 0 && (
        <div className="collaborator-list">
          {displayCollaborators.map((collab) => (
            <div key={collab.userId} className={`collaborator-item ${collab.isPending ? 'collaborator-pending' : ''}`}>
              <div className="collaborator-info">
                <div className={`collaborator-avatar ${collab.isPremium ? 'premium-avatar' : ''}`}>
                  {(collab.profilePicture || collab.photoURL) ? (
                    <img src={collab.profilePicture || collab.photoURL} alt="" className="collaborator-photo" />
                  ) : (
                    <span>{(collab.displayName || collab.username || 'U').charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="collaborator-name">
                  <span className="collaborator-display-name">
                    {collab.displayName || collab.username}
                    {collab.isPending && <span className="collaborator-pending-badge">new</span>}
                  </span>
                  {collab.username && (
                    <span className="collaborator-username">@{collab.username}</span>
                  )}
                </div>
                {!collab.isPending && (
                  <button
                    type="button"
                    className="btn-transfer"
                    onClick={() => handleTransferClick(collab)}
                    title="Transfer ownership"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M17 1l4 4-4 4"/>
                      <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                      <path d="M7 23l-4-4 4-4"/>
                      <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                    </svg>
                  </button>
                )}
              </div>
              <button
                type="button"
                className="btn-remove-collaborator"
                onClick={() => handleRemoveCollaborator(collab.userId)}
                title="Remove collaborator"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add collaborator picker */}
      {effectiveCollaboratorIds.length < MAX_COLLABORATORS && (
        <div className="collaborator-picker" ref={pickerRef}>
          <input
            ref={collaboratorInputRef}
            type="text"
            className="collaborator-search-input"
            placeholder="Add a friend as collaborator..."
            value={collaboratorSearch}
            onChange={(e) => {
              setCollaboratorSearch(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
          />
          <ContextualTooltip
            id="collaboration-first-invite"
            text="Collaborators can add places to your list."
            anchorRef={collaboratorInputRef}
            position="top"
            show={isOpen && effectiveCollaboratorIds.length === 0}
            delay={800}
          />
          {showDropdown && (
            <div className="collaborator-dropdown">
              {loadingFriends ? (
                <div className="collaborator-dropdown-message">Loading friends...</div>
              ) : friends.length === 0 ? (
                <div className="collaborator-dropdown-message">You don't have any friends yet.</div>
              ) : filteredFriends.length === 0 ? (
                <div className="collaborator-dropdown-message">No friends match your search</div>
              ) : (
                filteredFriends.map((friend) => (
                  <button
                    key={friend.id}
                    type="button"
                    className="collaborator-dropdown-item"
                    onClick={() => handleAddCollaborator(friend)}
                  >
                    <span className="collaborator-dropdown-name">{friend.displayName || friend.username}</span>
                    <span className="collaborator-dropdown-username">@{friend.username}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Transfer ownership confirmation dialog - rendered via portal to escape modal stacking context */}
      {showTransferConfirm && transferTarget && createPortal(
        <div className="transfer-confirm-overlay" onClick={() => setShowTransferConfirm(false)}>
          <div className="transfer-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Transfer Ownership</h3>
            <p>
              Are you sure you want to transfer ownership of this list to{' '}
              <strong>{transferTarget.displayName || transferTarget.username}</strong>?
            </p>
            <p className="transfer-warning">
              You will become a collaborator and they will become the owner.
            </p>
            <div className="transfer-confirm-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowTransferConfirm(false)}
                disabled={transferring}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-transfer-confirm"
                onClick={handleConfirmTransfer}
                disabled={transferring}
              >
                {transferring ? 'Transferring...' : 'Transfer Ownership'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

export default CollaboratorPicker;
